import { styled } from '@grupoboticario/flora-react-system'
import { CrossCircleIcon, CheckCircleIcon } from '@grupoboticario/flora-react-icons'

export const StyledSwitchWrapper = styled('label', {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  gap: '$2',

  variants: {
    disabled: {
      true: {
        cursor: 'not-allowed',
        pointerEvents: 'none',
        opacity: 0.5,
      },
    },
  },
})

export const StyledSwitchLabel = styled('span', {
  fontSize: '$bodyLarge',

  variants: {
    surfaceColor: {
      light: {
        color: '$nonInteractivePredominant',
      },
      dark: {
        color: '$nonInteractiveAltPredominant',
      },
    },
  },
})

export const StyledCrossCircleIcon = styled(CrossCircleIcon, {
  width: '1.25rem',
  height: '1.25rem',
  transitionProperty: 'background-color, color, transform, opacity',
  transitionDuration: '200ms',
  transitionTimingFunction: 'ease-out',
})

export const StyledCheckCircleIcon = styled(CheckCircleIcon, {
  width: '1.25rem',
  height: '1.25rem',
  transitionProperty: 'background-color, color, transform, opacity',
  transitionDuration: '200ms',
  transitionTimingFunction: 'ease-out',
})

/**
 * Criação da base do switch
 */
export const StyledFloraSwitch = styled('span', {
  position: 'relative',
  width: '3rem',
  height: '1.5rem',
  borderRadius: '1.25rem',
  transitionProperty: 'background-color, color, transform, opacity',
  transitionDuration: '200ms',
  transitionTimingFunction: 'ease-out',

  '& span': {
    zIndex: 1,
    position: 'absolute',
    left: '0.125rem',
    top: '0.125rem',
    transitionProperty: 'background-color, color, transform, opacity',
    transitionDuration: '200ms',
    transitionTimingFunction: 'ease-out',

    '& svg': {
      width: '1.25rem',
      transitionProperty: 'background-color, color, transform, opacity',
      transitionDuration: '200ms',
      transitionTimingFunction: 'ease-out',
    },
  },

  '&:after': {
    content: '""',
    position: 'absolute',
    top: '0.125rem',
    left: '0.125rem',
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    transitionProperty: 'background-color, color, transform, opacity',
    transitionDuration: '200ms',
    transitionTimingFunction: 'ease-out',
  },
})

export const StyledSwitchInput = styled('input', {
  opacity: 0,
  appearance: 'none',
  position: 'absolute',

  [`&:checked + ${StyledFloraSwitch}`]: {
    '&:after': {
      transform: 'translateX(1.5rem)',
    },
    [`& > span`]: {
      transform: 'translateX(1.5rem)',
    },
  },

  [`& + ${StyledFloraSwitch} > span`]: {
    transform: 'translateX(0)',
  },

  [`&:focus-visible + ${StyledFloraSwitch}`]: {
    outlineColor: '$keyboardFocusDefault',
    outlineStyle: 'solid',
    outlineWidth: '0.125rem',
    outlineOffset: '0.125rem',
  },

  /**
   * Seta a regra de disabled do switch para surfaceColor light
   */
  '&:disabled, &[aria-disabled="true"]': {
    pointerEvents: 'none',
    cursor: 'not-allowed',

    [`& + ${StyledFloraSwitch}, &:checked + ${StyledFloraSwitch}`]: {
      backgroundColor: '$disabledBackground',

      '&:after': {
        backgroundColor: '$disabledAltBackground',
      },

      [`${StyledCrossCircleIcon}`]: {
        color: '$disabledContent',
      },
    },
  },

  variants: {
    status: {
      neutral: {},
      success: {},
      error: {},
    },
    surfaceColor: {
      light: {},
      dark: {
        /**
         * Seta a regra de disabled do switch para surfaceColor dark
         */
        [`& + ${StyledSwitchLabel}`]: {
          color: '$nonInteractiveAltPredominant',
        },
        '&:disabled, &[aria-disabled="true"]': {
          [`& + ${StyledFloraSwitch}, &:checked + ${StyledFloraSwitch}`]: {
            backgroundColor: '$disabledAltBackground',

            '&:after': {
              backgroundColor: '$disabledBackground',
            },

            [`${StyledCrossCircleIcon}`]: {
              color: '$disabledAltContent',
            },
          },
        },
      },
    },
  },

  compoundVariants: [
    /**
     * Seta as props no componente status="neutral" e surfaceColor="light"
     */
    {
      status: 'neutral',
      surfaceColor: 'light',
      css: {
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$disabledBackground',

          '&:after': {
            backgroundColor: '$actionableContent',
          },
          '&:hover': {
            backgroundColor: '$disabledContent',

            [`${StyledCrossCircleIcon}`]: {
              color: '$actionableHover',
            },
          },
          [`${StyledCrossCircleIcon}`]: {
            color: '$actionableDefault',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$actionableDefault',

          '&:after': {
            backgroundColor: '$actionableContent',
          },
          '&:hover': {
            backgroundColor: '$actionableHover',
          },
          [`${StyledCheckCircleIcon}`]: {
            color: '$actionableDefault',
          },
        },
      },
    },
    /**
     * Seta as props no componente status="neutral" e surfaceColor="dark"
     */
    {
      status: 'neutral',
      surfaceColor: 'dark',
      css: {
        [`&:focus-visible + ${StyledFloraSwitch}`]: {
          outlineColor: '$keyboardFocusAlt',
        },
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$disabledAltBackground',

          [`${StyledCrossCircleIcon}`]: {
            color: '$actionableAlt',
          },
          '&:hover': {
            backgroundColor: '$disabledAltContent',

            [`${StyledCrossCircleIcon}`]: {
              color: '$actionableAltHover',
            },
          },
          '&:after': {
            backgroundColor: '$actionableAltContent',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$actionableAlt',

          [`${StyledCheckCircleIcon}`]: {
            color: '$actionableAlt',
          },
          '&:hover': {
            backgroundColor: '$actionableAltHover',

            [`span > ${StyledCheckCircleIcon}`]: {
              color: '$actionableAltHover',
            },
          },
          '&:after': {
            backgroundColor: '$actionableAltContent',
          },
        },
      },
    },
    /**
     * Seta as props no componente status="error" e surfaceColor="light"
     */
    {
      status: 'error',
      surfaceColor: 'light',
      css: {
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusErrorActionableDefault',

          [`${StyledCrossCircleIcon}`]: {
            color: '$statusErrorActionableDefault',
          },

          '&:hover': {
            backgroundColor: '$statusErrorActionableHover',

            [`${StyledCrossCircleIcon}`]: {
              color: '$statusErrorActionableHover',
            },
          },
          '&:after': {
            backgroundColor: '$actionableContent',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusSuccessActionableDefault',

          [`${StyledCheckCircleIcon}`]: {
            color: '$statusSuccessActionableDefault',
          },

          '&:after': {
            backgroundColor: '$actionableContent',
          },
          '&:hover': {
            backgroundColor: '$statusSuccessActionableHover',

            [`${StyledCheckCircleIcon}`]: {
              color: '$statusSuccessActionableHover',
            },
          },
        },
      },
    },
    /**
     * Seta as props no componente status="error" e surfaceColor="dark"
     */
    {
      status: 'error',
      surfaceColor: 'dark',
      css: {
        [`&:focus-visible + ${StyledFloraSwitch}`]: {
          outlineColor: '$keyboardFocusAlt',
        },
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusErrorActionableAlt',

          [`${StyledCrossCircleIcon}`]: {
            color: '$statusErrorActionableAlt',
          },

          '&:hover': {
            backgroundColor: '$statusErrorActionableAltHover',

            [`${StyledCrossCircleIcon}`]: {
              color: '$statusErrorActionableAltHover  ',
            },
          },
          '&:after': {
            backgroundColor: '$statusErrorActionableAltContent',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$colors$success-alt',

          [`${StyledCheckCircleIcon}`]: {
            color: '$colors$success-alt',
          },

          '&:after': {
            backgroundColor: '$statusSuccessActionableAltContent',
          },
          '&:hover': {
            backgroundColor: '$statusSuccessActionableAltHover',

            [`${StyledCheckCircleIcon}`]: {
              color: '$statusSuccessActionableAltHover',
            },
          },
        },
      },
    },
    /**
     * Seta as props no componente status="success" e surfaceColor="light"
     */
    {
      status: 'success',
      surfaceColor: 'light',
      css: {
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusErrorActionableDefault',

          [`${StyledCrossCircleIcon}`]: {
            color: '$statusErrorActionableDefault',
          },

          '&:hover': {
            backgroundColor: '$statusErrorActionableHover',

            [`${StyledCrossCircleIcon}`]: {
              color: '$statusErrorActionableHover',
            },
          },
          '&:after': {
            backgroundColor: '$actionableContent',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusSuccessActionableDefault',

          [`${StyledCheckCircleIcon}`]: {
            color: '$statusSuccessActionableDefault',
          },

          '&:after': {
            backgroundColor: '$actionableContent',
          },
          '&:hover': {
            backgroundColor: '$statusSuccessActionableHover',

            [`${StyledCheckCircleIcon}`]: {
              color: '$statusSuccessActionableHover',
            },
          },
        },
      },
    },
    /**
     * Seta as props no componente status="success" e surfaceColor="dark"
     */
    {
      status: 'success',
      surfaceColor: 'dark',
      css: {
        [`&:focus-visible + ${StyledFloraSwitch}`]: {
          outlineColor: '$keyboardFocusAlt',
        },
        [`& + ${StyledFloraSwitch}`]: {
          backgroundColor: '$statusErrorActionableAlt',

          [`${StyledCrossCircleIcon}`]: {
            color: '$statusErrorActionableAlt',
          },

          '&:hover': {
            backgroundColor: '$statusErrorActionableAltHover',

            [`${StyledCrossCircleIcon}`]: {
              color: '$statusErrorActionableAltHover  ',
            },
          },
          '&:after': {
            backgroundColor: '$statusErrorActionableAltContent',
          },
        },
        [`&:checked + ${StyledFloraSwitch}`]: {
          backgroundColor: '$colors$success-alt',

          [`${StyledCheckCircleIcon}`]: {
            color: '$colors$success-alt',
          },

          '&:after': {
            backgroundColor: '$statusSuccessActionableAltContent',
          },
          '&:hover': {
            backgroundColor: '$statusSuccessActionableAltHover',

            [`${StyledCheckCircleIcon}`]: {
              color: '$statusSuccessActionableAltHover',
            },
          },
        },
      },
    },
  ],
})
